import { getBuyButtonConfig } from '../utils/commonEntitlements';

/** @typedef {import('../types').ProductLandingConfig} ProductLandingConfig */

// Sent to ADP in the referral POST's Comments field: identifies the lead as 1099
// rather than general payroll, and carries the pricing quoted on this page.
//
// BE-6652 originally appended this pricing note to *every* ADP referral, and it was
// reverted (PR #1539) because the 1099 product had not shipped — ADP was receiving
// pricing claims nothing backed. It is scoped to this page's config precisely so the
// general /adp payroll referral stays untagged; the figures match the buy box and the
// pricing footnote on this page.
export const ADP_1099_REFERRAL_COMMENTS =
  'ADP 1099 Filing & Contractor Payments lead. 1099 contractor pricing: $40/month base + $4/contractor/month.';

/** @type {ProductLandingConfig} */
export const ADP_1099_CONFIG = {
  hero: {
    buyBox: [
      {
        bulletPoints: [
          { title: 'productPage.adp1099.buyBox.bulletPoint1' },
          { title: 'productPage.adp1099.buyBox.bulletPoint2' },
          { title: 'productPage.adp1099.buyBox.bulletPoint3' },
        ],
        productName: 'productPage.adp1099.buyBox.productName',
        staticPrice: 'productPage.adp1099.buyBox.price',
        priceNote: 'productPage.adp1099.buyBox.priceNote',
        veloCta: {
          displayVeloCta: true,
          promptMessageId: 'productPage.adp1099.buyBox.veloCta.prompt',
          topic: 'adp-1099-filing',
        },
        interactService: {
          ctaType: 'adpReferral',
          service: 'adp_referral',
          referralComments: ADP_1099_REFERRAL_COMMENTS,
        },
        note: 'productPage.adp1099.buyBox.note',
        noteLink: {
          text: 'productPage.adp1099.buyBox.noteLink',
          url: 'https://subscribe.adpinfo.com/',
        },
        termsLink: {
          message: 'productPage.adp1099.buyBox.termsLink',
          url: '#footnotes',
        },
      },
    ],
    headerPicture: {
      src: '/s2/raster/cafe-remote-working.png',
      alt: 'productPage.adp1099.heroHeaderPicture.altText',
    },
    headline: 'productPage.adp1099.page.headline',
    partnerLogo: {
      src: '/s2/raster/partner-adp-logo.png',
      alt: 'productPage.adp1099.heroPartnerLogo.altText',
    },
    partnerReviewBlanks: [],
    subheader: 'productPage.adp1099.page.subheader',
  },
  benefit: {
    title: 'productPage.adp1099.valueProp.title',
    items: [
      {
        icon: '/s2/material-icons/groupsrounded.svg',
        title: 'productPage.adp1099.valueProp.propTitle1',
        description: 'productPage.adp1099.valueProp.shortCopy1',
      },
      {
        icon: '/s2/material-icons/calculaterounded.svg',
        title: 'productPage.adp1099.valueProp.propTitle2',
        description: 'productPage.adp1099.valueProp.shortCopy2',
      },
      {
        icon: '/s2/material-icons/thumbuprounded.svg',
        title: 'productPage.adp1099.valueProp.propTitle3',
        description: 'productPage.adp1099.valueProp.shortCopy3',
      },
    ],
  },
  howItWorks: {
    title: 'productPage.adp1099.howItWorks.title',
    items: [
      {
        title: 'productPage.adp1099.howItWorks.step1',
        description: 'productPage.adp1099.howItWorks.shortDescription1',
      },
      {
        title: 'productPage.adp1099.howItWorks.step2',
        description: 'productPage.adp1099.howItWorks.shortDescription2',
      },
      {
        title: 'productPage.adp1099.howItWorks.step3',
        description: 'productPage.adp1099.howItWorks.shortDescription3',
      },
      {
        title: 'productPage.adp1099.howItWorks.step4',
        description: 'productPage.adp1099.howItWorks.shortDescription4',
      },
    ],
    image: '/s2/raster/baker-cafe-laptop-watch.png',
    alt: 'productPage.adp1099.howItWorks.altText',
    hasNumber: true,
    hasDivider: false,
    hasMobileImage: true,
  },
  whatsIncluded: {
    title: 'productPage.adp1099.whatsIncluded.title',
    items: [
      {
        title: 'productPage.adp1099.whatsIncluded.item1',
        description: 'productPage.adp1099.whatsIncluded.item1ShortDescription',
      },
      {
        title: 'productPage.adp1099.whatsIncluded.item2',
        description: 'productPage.adp1099.whatsIncluded.item2ShortDescription',
      },
      {
        title: 'productPage.adp1099.whatsIncluded.item3',
        description: 'productPage.adp1099.whatsIncluded.item3ShortDescription',
      },
    ],
    disclaimers: ['productPage.adp1099.whatsIncluded.disclaimer1', 'productPage.adp1099.whatsIncluded.disclaimer2'],
    image: '/s2/raster/carpenter-phone-smiling.png',
    alt: 'productPage.adp1099.whatsIncluded.altText',
    hasDivider: true,
    hasNumber: false,
    hasMobileImage: false,
  },
  footnotes: {
    divider: false,
    items: [
      {
        text: 'productPage.adp1099.footnotes.pricingTerms',
      },
      {
        text: 'productPage.adp1099.footnotes.disclaimer1',
      },
      {
        text: 'productPage.adp1099.footnotes.disclaimer2',
      },
    ],
  },
  assistance: {
    title: 'productPage.adp1099.needAssistance.title',
    getInTouch: 'productPage.adp1099.needAssistance.getInTouch',
    actions: 'productPage.adp1099.needAssistance.actions',
  },
  getBuyButtonConfig: (
    entitlements,
    service,
    _domesticJurisdiction,
    _formedBy,
    _formationStatus,
    ctaConfigContext = {},
  ) => {
    const { adpReferralSubmitted = false } = ctaConfigContext;

    const baseConfig = getBuyButtonConfig({
      entitlements,
      service,
      ctaText: 'productPage.adp1099.buyBox.buttonLabel.notSubmitted',
    });

    if (adpReferralSubmitted) {
      return {
        ...baseConfig,
        ctaDisabledTooltip: null,
        ctaText: 'productPage.adp1099.buyBox.buttonLabel.submitted',
        isBuyEnabled: false,
      };
    }

    return baseConfig;
  },
};
